import React, {useCallback, useEffect, useState} from "react";

import {Card} from "react-bootstrap";
import Select from "react-select";
import {loadAllClients, selectUserState} from "../../reducer/userSlice";
import {useDispatch, useSelector} from "react-redux";
import adminService from "../../services/admin.service";
import ajaxloader from "../../images/ajax-loader.gif";
import MessageBox from "../core/MessageBox";

export default function UserClients() {

    const dispatch = useDispatch();

    const {allClients} = useSelector(selectUserState);

    const [loadStatus, setLoadStatus] = useState("idle");
    const [loadMessage, setLoadMessage] = useState("");

    const [users, setUsers] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const [clientOptions, setClientOptions] = useState([]);
    const [selectedClients, setSelectedClients] = useState([]);

    const [saveStatus, setSaveStatus] = useState("idle");
    const [saveMessage, setSaveMessage] = useState("");
    const [saveSuccess, setSaveSuccess] = useState(false);

    const load = useCallback(() => {
        dispatch(loadAllClients());
        adminService.loadUsers().then(
            response => {
                setUsers(response.data["data"]);
                setLoadStatus("success");
            },
            error => {
                setLoadStatus("error");
                setLoadMessage("An error occurred while loading users");
            }
        );
    }, [dispatch]);

    useEffect(() => {
        if (loadStatus === "idle") {
            setLoadStatus("pending");
            load();
        }
    }, [loadStatus, load]);

    useEffect(() => {
        let options = [];
        allClients.forEach(client => {
            options.push({value: client.id, label: client.displayName + " (" + client.id + ")"});
        });
        setClientOptions(options);
    }, [allClients]);

    useEffect(() => {
        let options = users.map(u => ({
            value: u.userId,
            label: u.firstname + " " + u.lastname + " (" + u.email + ")" + (u.valid === 0 ? " (*** INACTIVE ***)" : ""),
        }));
        options.sort((a, b) => a.label.localeCompare(b.label));
        setUserOptions(options);
    }, [users]);

    const handleSelectUser = (option) => {
        setSelectedUser(option);
        setSelectedClients([]);
        setSaveStatus("idle");
        setSaveMessage("");

        if (option) {
            adminService.loadUserClients(option.value).then(
                response => {
                    const userClientIds = response.data["data"];
                    const preSelected = clientOptions.filter(co => userClientIds.includes(co.value));
                    setSelectedClients(preSelected);
                },
                error => {
                    setLoadMessage("An error occurred while loading user's clients");
                }
            );
        }
    };

    const handleSave = () => {
        if (!selectedUser) return;

        setSaveStatus("pending");
        setSaveMessage("");
        setSaveSuccess(false);

        const clientIds = selectedClients.map(c => c.value);
        adminService.saveUserClients(selectedUser.value, clientIds).then(
            response => {
                setSaveStatus("success");
                setSaveMessage(response.data.message || "Clients updated successfully");
                setSaveSuccess(true);
            },
            error => {
                setSaveStatus("error");
                const errorData = error.response && error.response.data;
                setSaveMessage((errorData && errorData.message) || error.toString());
                setSaveSuccess(false);
            }
        );
    };

    let content;
    if (loadStatus === "error") {
        content = <div className="main-content">
            <header className="jumbotron">
                <h3 className='canopia'>Error</h3>
                <p>{loadMessage}</p>
            </header>
        </div>;
    } else if (loadStatus !== "success") {
        content = <div><br/><img alt="loading" src={ajaxloader}/></div>;
    } else {
        content =
            <Card className={'canopia-card'} style={{width: '40rem'}}>
                <Card.Body>
                    <Card.Title className='canopia-card-title'>
                        User Clients
                    </Card.Title>

                    <div className="form-group">
                        <label>User</label>
                        <Select
                            options={userOptions}
                            value={selectedUser}
                            isClearable
                            isSearchable={true}
                            onChange={handleSelectUser}
                            placeholder="Select a user..."
                        />
                    </div>
                    <br/>

                    <div className="form-group">
                        <label>Client(s)</label>
                        <Select
                            options={clientOptions}
                            value={selectedClients}
                            isClearable
                            isSearchable={true}
                            closeMenuOnSelect={false}
                            onChange={setSelectedClients}
                            isMulti
                            isDisabled={!selectedUser}
                            placeholder={selectedUser ? "Select clients..." : "Select a user first"}
                        />
                    </div>
                    <br/>

                    <div className="form-group">
                        <button
                            className="btn btn-primary btn-block"
                            onClick={handleSave}
                            disabled={!selectedUser || saveStatus === "pending"}
                        >
                            {saveStatus === "pending" ? "Saving..." : "Save"}
                        </button>
                    </div>

                    {saveMessage && (
                        <>
                        {saveSuccess ?
                            <MessageBox
                                title={saveMessage}
                                colorClass={'canopia7'}
                            />
                            :
                            <MessageBox
                                title={'Error'}
                                message={saveMessage}
                                colorClass={'canopia6'}
                            />
                        }
                        </>
                    )}
                </Card.Body>
            </Card>;
    }

    return (
        <div className="main-content">
            {content}
        </div>
    );
}
